/* eslint-disable max-lines */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { isNil } from 'lodash';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  bulkLockItemsForPhysicalCountApi,
  bulkUnlockItemsFromPhysicalCountApi,
  createCountApi,
  listInventoryItemsApi,
  lockItemForPhysicalCountApi,
  unlockItemFromPhysicalCountApi,
} from 'api/inventory';
import colors from 'common/constants/colors';
import useRoles from 'common/hooks/useRoles';
import UnverifiedCell from 'common/components/UnverifiedCell';
import { selectAuthorizedFacilities } from 'store/selectors/facilitySelectors';
import { handleError } from 'store/thunks/errorHandlerThunks';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import { useInventoryAlert } from '../context/InventoryAlertContext';
import { dashIfEmpty } from '../utils/columnFormatters';
import AppliedFiltersHeader from './AppliedFiltersHeader';
import CountHistoryModal from './CountHistoryModal';
import LotSummaryDrawer from './LotSummaryDrawer';
import ManageCountDialog from './ManageCountDialog';
import ManualAdjustLotSelectorDialog from './ManualAdjustLotSelectorDialog';
import ManualAdjustmentButton from './ManualAdjustmentButton';
import ManualAdjustmentDialog from './ManualAdjustmentDialog';
import ReorderConfigDialog from './ReorderConfigDialog';
import StartCountConfirmDialog from './StartCountConfirmDialog';
import TransactionHistoryModal from './TransactionHistoryModal';

const EMPTY_DRAWER_ITEMS = [];
const NUM_CELL = { textAlign: 'right' };
// AG Grid's header height at this row height. Shared with the skeleton and with the refresh bar,
// which sits directly beneath the header rather than above it, so the header stays readable.
const GRID_HEADER_HEIGHT = 48;

const roundNumFmt = ({ value }) => (isNil(value) ? '—' : Math.round(Number(value)).toString());

const ExpiringSoonCell = ({ value }) =>
  value > 0 ? (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 0.5 }}>
      <WarningAmberIcon sx={{ color: '#FFC107' }} fontSize='small' />
      <Box component='span' sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#FFC107' }}>
        {value} {value === 1 ? 'lot' : 'lots'}
      </Box>
    </Box>
  ) : null;

ExpiringSoonCell.propTypes = { value: PropTypes.number };
ExpiringSoonCell.defaultProps = { value: 0 };

const SKELETON_COLS = [
  { key: 'ndc', flex: 1.5 },
  { key: 'drug', flex: 3 },
  { key: 'qty', flex: 1 },
  { key: 'status', flex: 1.5 },
  { key: 'expiry', flex: 1.2 },
  { key: 'lots', flex: 1 },
  { key: 'txn', flex: 1 },
];
const SKELETON_ROW_KEYS = [
  'r0',
  'r1',
  'r2',
  'r3',
  'r4',
  'r5',
  'r6',
  'r7',
  'r8',
  'r9',
  'r10',
  'r11',
];

const TableSkeleton = () => (
  <Grid component={Paper} sx={{ width: '100%', overflow: 'hidden' }}>
    <Box sx={{ height: '65vh', width: '100%' }}>
      <Box
        sx={{
          backgroundColor: 'primary.main',
          height: GRID_HEADER_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          px: 2,
          gap: 2,
        }}
      >
        {SKELETON_COLS.map(({ key, flex }) => (
          <Skeleton
            key={key}
            variant='text'
            sx={{ flex, backgroundColor: 'rgba(255,255,255,0.15)' }}
            height={18}
          />
        ))}
      </Box>
      {SKELETON_ROW_KEYS.map((rowKey) => (
        <Box
          key={rowKey}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            gap: 2,
            height: 42,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {SKELETON_COLS.map(({ key, flex }) => (
            <Skeleton key={key} variant='text' sx={{ flex }} height={14} />
          ))}
        </Box>
      ))}
    </Box>
  </Grid>
);

const STOCK_STATUS_CONFIG = {
  HEALTHY: { label: 'HEALTHY', color: '#2e7d32', bgColor: '#e8f5e9' },
  LOW_STOCK: { label: 'LOW', color: '#e65100', bgColor: '#fff3e0' },
  CRITICAL_STOCK: { label: 'CRITICAL', color: '#c62828', bgColor: '#fdecea' },
  OUT_OF_STOCK: { label: 'OUT OF STOCK', color: '#7f1d1d', bgColor: '#fde8e8' },
  // No dispensing in the last 14 days, so core could derive no LOW/CRITICAL
  // threshold for the item. Neutral grey on purpose: this means "not assessed",
  // not "fine" -- it used to arrive as a green HEALTHY chip. Do not style it as an
  // alert either; nothing is known to be wrong, we simply cannot say.
  NO_RECENT_MOVEMENT: { label: 'NO MOVEMENT', color: '#616161', bgColor: '#f5f5f5' },
};

const StockStatusChip = ({ value }) => {
  const config = STOCK_STATUS_CONFIG[value];
  if (!config) return null;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 20,
        px: 1,
        borderRadius: '20px',
        border: `1px solid ${config.color}`,
        backgroundColor: config.bgColor,
        color: config.color,
        fontWeight: 700,
        fontSize: '0.65rem',
        whiteSpace: 'nowrap',
        letterSpacing: '0.03em',
      }}
    >
      {config.label}
    </Box>
  );
};

StockStatusChip.propTypes = { value: PropTypes.string };
StockStatusChip.defaultProps = { value: null };

const QTY_COLOR = { OUT_OF_STOCK: '#7f1d1d', CRITICAL_STOCK: '#c62828', LOW_STOCK: '#e65100' };

const QtyCell = ({ value, data }) => {
  const color = QTY_COLOR[data?.stockStatus] ?? 'inherit';
  const isLocked = data?.isLockForCount;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.5,
        color,
        fontWeight: color !== 'inherit' ? 600 : 400,
      }}
    >
      {isLocked && (
        <Tooltip title='Locked for physical count' placement='bottom' arrow>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              px: 1,
              height: 22,
              borderRadius: '4px',
              bgcolor: '#F3F4F6',
              border: '1px solid #D1D5DB',
              flexShrink: 0,
            }}
          >
            <LockIcon sx={{ fontSize: '0.75rem', color: '#6B7280' }} />
            <Typography
              sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#6B7280', lineHeight: 1 }}
            >
              LOCKED
            </Typography>
          </Box>
        </Tooltip>
      )}
      {isNil(value) ? '—' : Number(value).toString()}
    </Box>
  );
};

QtyCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  data: PropTypes.shape({ stockStatus: PropTypes.string, isLockForCount: PropTypes.bool }),
};
QtyCell.defaultProps = { value: null, data: null };

const LotCountButton = ({ value, data, onLotsClick }) => (
  <Box
    component='button'
    onClick={() => onLotsClick?.(data)}
    sx={{
      background: 'none',
      border: 'none',
      padding: 0,
      font: 'inherit',
      cursor: 'pointer',
      color: 'primary.main',
      fontWeight: 600,
      textDecoration: 'underline',
    }}
  >
    {value ?? 0}
  </Box>
);

LotCountButton.propTypes = {
  value: PropTypes.number,
  data: PropTypes.shape({}),
  onLotsClick: PropTypes.func,
};
LotCountButton.defaultProps = { value: 0, data: null, onLotsClick: null };

const ViewHistoryButton = ({ data, onHistoryClick }) => (
  <Box
    component='button'
    onClick={() => onHistoryClick?.(data)}
    sx={{
      background: 'none',
      border: 'none',
      padding: 0,
      font: 'inherit',
      cursor: 'pointer',
      color: 'primary.main',
      fontWeight: 600,
      textDecoration: 'underline',
    }}
  >
    View
  </Box>
);

ViewHistoryButton.propTypes = {
  data: PropTypes.shape({}),
  onHistoryClick: PropTypes.func,
};
ViewHistoryButton.defaultProps = { data: null, onHistoryClick: null };

const sideBar = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
      },
    },
    'filters',
  ],
};

const defaultColDef = {
  resizable: true,
  filter: 'agTextColumnFilter',
  suppressMovable: false,
  suppressHeaderMenuButton: true,
  wrapHeaderText: true,
  autoHeaderHeight: true,
};

const DrugNameCell = ({ value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
    <span>{value}</span>
  </Box>
);

DrugNameCell.propTypes = { value: PropTypes.string };
DrugNameCell.defaultProps = { value: '' };

const NdcCell = ({ value, data }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      height: '100%',
      fontFamily: 'monospace',
      fontSize: 13,
    }}
  >
    {data?.isLockForCount && (
      <Tooltip title='Locked for physical count' placement='bottom' arrow>
        <LockIcon sx={{ fontSize: '0.85rem', color: '#6B7280', flexShrink: 0 }} />
      </Tooltip>
    )}
    <span>{value}</span>
  </Box>
);

NdcCell.propTypes = {
  value: PropTypes.string,
  data: PropTypes.shape({ isLockForCount: PropTypes.bool }),
};
NdcCell.defaultProps = { value: '', data: null };

const getRowStyle = ({ data }) => {
  if (data?.isLockForCount) return { backgroundColor: '#F3F4F6' };
  if (data?.stockStatus === 'OUT_OF_STOCK') return { backgroundColor: '#FEE2E2' };
  if (data?.stockStatus === 'CRITICAL_STOCK') return { backgroundColor: '#FEF2F2' };
  if (data?.stockStatus === 'LOW_STOCK') return { backgroundColor: '#FFFBEB' };
  return undefined;
};

const getSelectionMode = ({ total, lockedCount, unlockedCount }) => {
  if (total === 0) return null;
  if (total > 50) return 'over_cap';
  if (lockedCount > 0 && unlockedCount > 0) return 'mixed';
  if (lockedCount > 0) return 'all_locked';
  return 'all_unlocked';
};

const BULK_BAR_CONFIG = {
  all_unlocked: {
    accent: '#2563EB',
    border: '#BFDBFE',
    bg: '#EFF6FF',
    labelColor: '#1e40af',
    label: (n) => `${n} Item${n !== 1 ? 's' : ''} Selected`,
  },
  all_locked: {
    accent: '#D97706',
    border: '#FED7AA',
    bg: '#FFF7ED',
    labelColor: '#92400E',
    label: (n) => `${n} Locked Item${n !== 1 ? 's' : ''} Selected`,
  },
  mixed: {
    accent: '#DC2626',
    border: '#FECACA',
    bg: '#FEF2F2',
    labelColor: '#B91C1C',
    label: (n) => `${n} Item${n !== 1 ? 's' : ''} Selected`,
  },
  over_cap: {
    accent: '#DC2626',
    border: '#FECACA',
    bg: '#FEF2F2',
    labelColor: '#B91C1C',
    label: (n) => `${n} Items Selected`,
  },
};

const BulkSelectionBar = ({ selectedCount, selectionMode, onLock, onUnlock, onClear }) => {
  if (!selectionMode) return null;
  const cfg = BULK_BAR_CONFIG[selectionMode];
  if (!cfg) return null;

  const icon =
    selectionMode === 'mixed' ? (
      <WarningAmberIcon sx={{ fontSize: '1rem', color: cfg.accent, flexShrink: 0 }} />
    ) : (
      <LockIcon sx={{ fontSize: '1rem', color: cfg.accent, flexShrink: 0 }} />
    );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 0.875,
        mb: 1,
        bgcolor: cfg.bg,
        border: `1px solid ${cfg.border}`,
        borderLeft: `3px solid ${cfg.accent}`,
        borderRadius: '6px',
      }}
    >
      {icon}
      <Typography variant='body2' sx={{ fontWeight: 600, color: cfg.labelColor }}>
        {cfg.label(selectedCount)}
      </Typography>

      {selectionMode === 'mixed' && (
        <Typography variant='body2' sx={{ color: '#B91C1C' }}>
          — Mix of locked and unlocked, select only one type
        </Typography>
      )}

      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
        {selectionMode === 'all_unlocked' && (
          <Button
            variant='contained'
            size='small'
            startIcon={<LockIcon />}
            onClick={onLock}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
          >
            Lock Items
          </Button>
        )}

        {selectionMode === 'over_cap' && (
          <Tooltip title='Maximum 50 items per bulk operation' placement='top'>
            <span>
              <Button
                variant='contained'
                size='small'
                disabled
                startIcon={<LockIcon />}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
              >
                Lock Items
              </Button>
            </span>
          </Tooltip>
        )}

        {selectionMode === 'all_locked' && (
          <Button
            variant='contained'
            size='small'
            startIcon={<LockOpenIcon />}
            onClick={onUnlock}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.75rem',
              bgcolor: '#D97706',
              '&:hover': { bgcolor: '#B45309' },
            }}
          >
            Cancel Lock
          </Button>
        )}

        <Button
          variant='text'
          size='small'
          onClick={onClear}
          sx={{ color: '#6b7280', textTransform: 'none' }}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
};

BulkSelectionBar.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  selectionMode: PropTypes.oneOf(['all_unlocked', 'all_locked', 'mixed', 'over_cap']),
  onLock: PropTypes.func.isRequired,
  onUnlock: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};
BulkSelectionBar.defaultProps = { selectionMode: null };

const InventoryDashboardFacilityTable = ({ facilityId }) => {
  const gridRef = useRef(null);
  const dispatch = useDispatch();
  const { state: alertState, dispatch: alertDispatch } = useInventoryAlert();
  const { enqueueSnackbar } = useSnackbar();
  // Locking freezes fulfilment, so the bulk path carries the same permission as starting a
  // single count. Row selection exists only to feed the lock/unlock bar, so it goes too —
  // leaving the checkboxes would offer a selection with nothing to do with it.
  const { performPhysicalCount: canManagePhysicalCount } = useRoles();
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedTransactionItem, setSelectedTransactionItem] = useState(null);
  const [selectorDialog, setSelectorDialog] = useState(null);
  const [adjustDialog, setAdjustDialog] = useState(null);
  const [reorderDialog, setReorderDialog] = useState(null);
  const [lotsRefreshKey, setLotsRefreshKey] = useState(0);
  const [startCountItem, setStartCountItem] = useState(null);
  const [manageCountItem, setManageCountItem] = useState(null);
  const [countHistoryItem, setCountHistoryItem] = useState(null);
  const [cancelLockConfirmItem, setCancelLockConfirmItem] = useState(null);
  const [startCountLoading, setStartCountLoading] = useState(false);
  const [submitCountLoading, setSubmitCountLoading] = useState(false);
  const [cancelCountLoading, setCancelCountLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkLockConfirmOpen, setBulkLockConfirmOpen] = useState(false);
  const [bulkUnlockConfirmOpen, setBulkUnlockConfirmOpen] = useState(false);
  const [bulkOperationLoading, setBulkOperationLoading] = useState(false);

  // Resolved against the user's own facilities, so a facility they do not hold has no name to
  // render here even if its id reaches this table (POM-6274).
  const facilities = useSelector(selectAuthorizedFacilities);
  const facility = useMemo(
    () => facilities.find((p) => p.fulfillingPharmacyId === facilityId),
    [facilities, facilityId]
  );
  const pharmacyName = facility?.name ?? '';
  const pharmacyState = facility?.state ?? '';

  const handleHistoryClick = useCallback((data) => setSelectedTransactionItem(data), []);

  const handleReorderConfigClick = useCallback(
    (data) =>
      setReorderDialog({
        inventoryItemId: data.inventoryItemId,
        ndc: data.ndc,
        drugName: data.drugName,
        lowStockPercent: data.lowStockPercent,
        criticalStockPercent: data.criticalStockPercent,
        leadTime: data.leadTime,
      }),
    []
  );

  // Tracks whether an adjustment was actually saved during this selector session,
  // so closing the selector only refreshes the grid when something changed — a
  // plain open/close no longer triggers a needless reload.
  const adjustmentMadeRef = useRef(false);

  const handleAdjustClick = useCallback((data) => {
    adjustmentMadeRef.current = false;
    setSelectorDialog({
      ndc: data.ndc,
      drugName: data.drugName,
      manufacturer: data.manufacturer,
      inventoryItemId: data.inventoryItemId,
    });
  }, []);

  const handleStartCountClick = useCallback((data) => setStartCountItem(data), []);
  const handleManageCountClick = useCallback((data) => setManageCountItem(data), []);
  const handleCountHistoryClick = useCallback((data) => setCountHistoryItem(data), []);
  const handleCancelCountDirectClick = useCallback((data) => setCancelLockConfirmItem(data), []);

  const handleConfirmStartCount = useCallback(async () => {
    if (!startCountItem) return;
    setStartCountLoading(true);
    try {
      const lockResponse = await lockItemForPhysicalCountApi({
        inventoryItemId: startCountItem.inventoryItemId,
        ndc: startCountItem.ndc,
        fulfillingPharmacyId: startCountItem.fulfillingPharmacy?.fulfillingPharmacyId,
      });
      // Open ManageCountDialog immediately; table refreshes silently in background
      setStartCountItem(null);
      setManageCountItem({ ...startCountItem, quantityOnHand: lockResponse.systemQty });
      alertDispatch({ type: 'REFRESH_SUMMARY' });
      setRefreshKey((k) => k + 1);
    } catch (error) {
      enqueueSnackbar('Failed to start count', { variant: 'error' });
      dispatch(handleError({ error }));
    } finally {
      setStartCountLoading(false);
    }
  }, [startCountItem, enqueueSnackbar, dispatch, alertDispatch]);

  const handleSubmitCount = useCallback(
    async ({ inventoryItemId, lotCounts }) => {
      setSubmitCountLoading(true);
      try {
        await createCountApi({ inventoryItemId, lotCounts });
        enqueueSnackbar('Physical count submitted successfully', { variant: 'success' });
        setManageCountItem(null);
        alertDispatch({ type: 'REFRESH_SUMMARY' });
        setRefreshKey((k) => k + 1);
      } catch (error) {
        enqueueSnackbar('Failed to submit count', { variant: 'error' });
        dispatch(handleError({ error }));
      } finally {
        setSubmitCountLoading(false);
      }
    },
    [enqueueSnackbar, dispatch, alertDispatch]
  );

  const handleCancelCount = useCallback(
    async ({ inventoryItemId }) => {
      setCancelCountLoading(true);
      try {
        await unlockItemFromPhysicalCountApi({ inventoryItemId });
        enqueueSnackbar('Physical count cancelled', { variant: 'success' });
        setManageCountItem(null);
        alertDispatch({ type: 'REFRESH_SUMMARY' });
        setRefreshKey((k) => k + 1);
      } catch (error) {
        enqueueSnackbar('Failed to cancel count', { variant: 'error' });
        dispatch(handleError({ error }));
      } finally {
        setCancelCountLoading(false);
      }
    },
    [enqueueSnackbar, dispatch, alertDispatch]
  );

  const handleLotSelect = useCallback(
    (lot, fulfillingPharmacyId, selectedFacilityName) => {
      setAdjustDialog({
        lot,
        ndc: selectorDialog.ndc,
        drugName: selectorDialog.drugName,
        fulfillingPharmacyId,
        facilityName: selectedFacilityName,
      });
      // Selector stays open behind the adjustment dialog
    },
    [selectorDialog]
  );

  const handleAdjusted = useCallback(() => {
    adjustmentMadeRef.current = true;
    setAdjustDialog(null);
    setLotsRefreshKey((k) => k + 1);
    // Table refresh deferred to selector close — avoids unmounting the selector during loading
  }, []);

  const handleSelectorClose = useCallback(() => {
    setSelectorDialog(null);
    // Only refresh the grid if an adjustment was actually made in this session.
    if (adjustmentMadeRef.current) {
      adjustmentMadeRef.current = false;
      setRefreshKey((k) => k + 1);
    }
  }, []);

  // The Lot Summary drawer gets the same treatment as the selector above: it reloads its own
  // lots on an adjustment and the grid waits until the drawer is closed, so the operator is not
  // watching a panel reload underneath them for a row the grid is only going to restate.
  const lotSummaryAdjustedRef = useRef(false);

  const handleLotSummaryAdjusted = useCallback(() => {
    lotSummaryAdjustedRef.current = true;
  }, []);

  const handleLotSummaryClose = useCallback(() => {
    setSelectedItem(null);
    if (lotSummaryAdjustedRef.current) {
      lotSummaryAdjustedRef.current = false;
      setRefreshKey((k) => k + 1);
    }
  }, []);

  const handleSelectionChanged = useCallback(() => {
    if (gridRef.current?.api) {
      setSelectedRows(gridRef.current.api.getSelectedRows());
    }
  }, []);

  const clearSelection = useCallback(() => {
    gridRef.current?.api?.deselectAll();
    setSelectedRows([]);
  }, []);

  const lockedSelected = selectedRows.filter((r) => r.isLockForCount);
  const unlockedSelected = selectedRows.filter((r) => !r.isLockForCount);
  const selectionMode = getSelectionMode({
    total: selectedRows.length,
    lockedCount: lockedSelected.length,
    unlockedCount: unlockedSelected.length,
  });

  const handleBulkLock = useCallback(async () => {
    setBulkOperationLoading(true);
    try {
      const result = await bulkLockItemsForPhysicalCountApi(
        unlockedSelected.map((row) => ({
          inventoryItemId: row.inventoryItemId,
          ndc: row.ndc,
          fulfillingPharmacyId: row.fulfillingPharmacy?.fulfillingPharmacyId ?? facilityId,
        }))
      );
      const lockedCount = result?.locked?.length ?? 0;
      const failedCount = result?.failed?.length ?? 0;
      clearSelection();
      enqueueSnackbar(
        `${lockedCount} item${lockedCount !== 1 ? 's' : ''} locked for physical count${
          failedCount > 0 ? `, ${failedCount} failed` : ''
        }`,
        { variant: failedCount > 0 ? 'warning' : 'success' }
      );
      alertDispatch({ type: 'REFRESH_SUMMARY' });
    } catch (error) {
      dispatch(handleError({ error }));
    } finally {
      setBulkOperationLoading(false);
      setBulkLockConfirmOpen(false);
      setRefreshKey((k) => k + 1);
    }
  }, [unlockedSelected, clearSelection, enqueueSnackbar, facilityId, dispatch, alertDispatch]);

  const handleBulkUnlock = useCallback(async () => {
    setBulkOperationLoading(true);
    try {
      const result = await bulkUnlockItemsFromPhysicalCountApi(
        lockedSelected.map((row) => row.inventoryItemId)
      );
      const unlockedCount = result?.unlocked?.length ?? 0;
      const failedCount = result?.failed?.length ?? 0;
      clearSelection();
      enqueueSnackbar(
        `${unlockedCount} item${unlockedCount !== 1 ? 's' : ''} unlocked${
          failedCount > 0 ? `, ${failedCount} failed` : ''
        }`,
        { variant: failedCount > 0 ? 'warning' : 'success' }
      );
      alertDispatch({ type: 'REFRESH_SUMMARY' });
    } catch (error) {
      dispatch(handleError({ error }));
    } finally {
      setBulkOperationLoading(false);
      setBulkUnlockConfirmOpen(false);
      setRefreshKey((k) => k + 1);
    }
  }, [lockedSelected, clearSelection, enqueueSnackbar, dispatch, alertDispatch]);

  const columnDefs = useMemo(
    () => [
      ...(canManagePhysicalCount
        ? [
            {
              field: 'isLockForCount',
              headerName: '',
              width: 44,
              minWidth: 44,
              maxWidth: 44,
              pinned: 'left',
              sortable: false,
              filter: false,
              resizable: false,
              suppressMovable: true,
              headerCheckboxSelection: true,
              checkboxSelection: true,
              cellRenderer: () => null,
              cellStyle: { display: 'flex', alignItems: 'center', paddingLeft: '12px' },
            },
          ]
        : []),
      {
        field: 'ndc',
        headerName: 'NDC',
        minWidth: 140,
        pinned: 'left',
        cellRenderer: NdcCell,
      },
      {
        field: 'drugName',
        headerName: 'Drug Name',
        flex: 2,
        minWidth: 200,
        cellRenderer: DrugNameCell,
        initialSort: 'asc',
      },
      {
        // POM-6112 — the all-facilities grid has carried Product ID since POM-5910; picking a
        // single facility dropped it, so the one view where a locked product's sibling NDCs sit
        // next to each other was the one view that could not show they are one product.
        field: 'productId',
        headerName: 'Product ID',
        minWidth: 120,
        cellStyle: { fontFamily: 'monospace', fontSize: 13 },
        valueFormatter: dashIfEmpty,
      },
      { field: 'strength', headerName: 'Strength', minWidth: 90 },
      {
        // POM-5910 — manufacturer is per-NDC inventory-master metadata. Rows here
        // are already one per NDC per facility, so the value maps directly.
        field: 'manufacturer',
        headerName: 'Manufacturer',
        minWidth: 140,
        valueFormatter: dashIfEmpty,
      },
      {
        field: 'quantityOnHand',
        headerName: 'On Hand',
        minWidth: 50,
        cellRenderer: QtyCell,
      },
      {
        field: 'stockStatus',
        headerName: 'Stock Status',
        minWidth: 100,
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        cellRenderer: StockStatusChip,
      },
      {
        field: 'expiringSoonCount',
        headerName: 'Expiring Soon',
        minWidth: 100,
        cellRenderer: ExpiringSoonCell,
      },
      {
        field: 'unverifiedQty',
        headerName: SYSTEM_DEFICIT,
        minWidth: 100,
        sortable: true,
        // A number filter, not the grid's default text one: the question this column exists
        // to answer is "greater than 0", and a text filter cannot ask it.
        filter: 'agNumberColumnFilter',
        cellRenderer: UnverifiedCell,
      },
      {
        field: 'lotCount',
        headerName: 'Available Lots',
        minWidth: 80,
        cellRenderer: LotCountButton,
        cellRendererParams: { onLotsClick: setSelectedItem },
      },
      {
        field: 'inventoryItemId',
        headerName: 'Transactions',
        minWidth: 60,
        sortable: false,
        filter: false,
        cellRenderer: ViewHistoryButton,
        cellRendererParams: { onHistoryClick: handleHistoryClick },
      },
      {
        field: 'avgDailyDispense',
        headerName: 'Avg Daily Dispense',
        minWidth: 80,
        cellStyle: NUM_CELL,
        valueFormatter: roundNumFmt,
      },
      {
        field: 'totalDispensed14d',
        headerName: 'Dispensed (14d)',
        minWidth: 80,
        cellStyle: NUM_CELL,
        valueFormatter: roundNumFmt,
      },
      {
        field: 'inventoryItemId',
        headerName: '',
        // The one header cell with nothing in it, and the only spot for a grid-wide control that
        // does not cost the banner above a row of height.
        headerComponent: AppliedFiltersHeader,
        headerClass: 'inventory-actions-header',
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        sortable: false,
        filter: false,
        resizable: false,
        pinned: 'right',
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        cellRenderer: ManualAdjustmentButton,
        cellRendererParams: {
          showPhysicalCount: true,
          onAdjustClick: handleAdjustClick,
          onReorderConfigClick: handleReorderConfigClick,
          onStartCountClick: handleStartCountClick,
          onManageCountClick: handleManageCountClick,
          onCountHistoryClick: handleCountHistoryClick,
          onCancelCountClick: handleCancelCountDirectClick,
        },
      },
    ],
    [
      canManagePhysicalCount,
      handleHistoryClick,
      handleAdjustClick,
      handleReorderConfigClick,
      handleStartCountClick,
      handleManageCountClick,
      handleCountHistoryClick,
      handleCancelCountDirectClick,
    ]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    listInventoryItemsApi({ filter: { fulfillingPharmacyId: facilityId } })
      .then((result) => {
        if (cancelled) return;
        setItems(result ?? []);
      })
      .catch((error) => {
        if (cancelled) return;
        setItems([]);
        enqueueSnackbar('Failed to load inventory items. Please try again.', { variant: 'error' });
        dispatch(handleError({ error }));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [facilityId, refreshKey, alertState.summaryRefreshKey, dispatch, enqueueSnackbar]);

  const rowData = useMemo(() => items ?? [], [items]);

  // A fresh array literal would re-run LotSummaryDrawer's fetch effect on every parent render,
  // since that effect keys on the `items` prop.
  const drawerItems = selectedItem ? [selectedItem] : EMPTY_DRAWER_ITEMS;

  // Only the first load blanks the grid. A refresh keeps it mounted, because unmounting is what
  // discarded the column filter, the sort, the column widths and the open Filters panel — none of
  // which the grid can restore afterwards (POM-6140). It is also what tore down the Lot Summary
  // drawer mid-adjustment, which is why the selector flow below already defers its refresh.
  if (isNil(items)) return <TableSkeleton />;

  return (
    <>
      <LotSummaryDrawer
        items={drawerItems}
        onClose={handleLotSummaryClose}
        onAdjusted={handleLotSummaryAdjusted}
      />

      <TransactionHistoryModal
        open={Boolean(selectedTransactionItem)}
        ndc={selectedTransactionItem?.ndc}
        drugName={selectedTransactionItem?.drugName}
        manufacturer={selectedTransactionItem?.manufacturer}
        fulfillingPharmacyId={facilityId}
        pharmacyName={pharmacyName}
        quantityOnHand={selectedTransactionItem?.quantityOnHand}
        onClose={() => setSelectedTransactionItem(null)}
      />

      <ManualAdjustLotSelectorDialog
        open={Boolean(selectorDialog)}
        ndc={selectorDialog?.ndc}
        drugName={selectorDialog?.drugName}
        manufacturer={selectorDialog?.manufacturer}
        facilityOptions={[
          {
            fulfillingPharmacyId: facilityId,
            inventoryItemId: selectorDialog?.inventoryItemId,
            name: pharmacyName,
            state: pharmacyState,
          },
        ]}
        initialFacilityId={facilityId}
        initialInventoryItemId={selectorDialog?.inventoryItemId}
        lotsRefreshKey={lotsRefreshKey}
        onLotSelect={handleLotSelect}
        onClose={handleSelectorClose}
      />

      <ManualAdjustmentDialog
        open={Boolean(adjustDialog)}
        lot={adjustDialog?.lot ?? null}
        drugName={adjustDialog?.drugName ?? ''}
        ndc={adjustDialog?.ndc ?? ''}
        fulfillingPharmacyId={adjustDialog?.fulfillingPharmacyId ?? ''}
        facilityName={adjustDialog?.facilityName ?? ''}
        onSaveSuccess={handleAdjusted}
        onClose={() => setAdjustDialog(null)}
      />

      <ReorderConfigDialog
        open={Boolean(reorderDialog)}
        inventoryItemId={reorderDialog?.inventoryItemId ?? ''}
        ndc={reorderDialog?.ndc ?? ''}
        drugName={reorderDialog?.drugName ?? ''}
        facilityName={pharmacyName}
        lowStockPercent={reorderDialog?.lowStockPercent ?? null}
        criticalStockPercent={reorderDialog?.criticalStockPercent ?? null}
        leadTime={reorderDialog?.leadTime ?? null}
        onSaveSuccess={() => setRefreshKey((k) => k + 1)}
        onClose={() => setReorderDialog(null)}
      />

      {!manageCountItem && (
        <StartCountConfirmDialog
          open={Boolean(startCountItem)}
          onClose={() => setStartCountItem(null)}
          onConfirm={handleConfirmStartCount}
          itemData={
            startCountItem
              ? {
                  drugName: startCountItem.drugName,
                  strength: startCountItem.strength,
                  ndc: startCountItem.ndc,
                  manufacturer: startCountItem.manufacturer,
                  facilityName: startCountItem.fulfillingPharmacy?.name ?? pharmacyName,
                }
              : null
          }
          loading={startCountLoading}
        />
      )}

      <ManageCountDialog
        open={Boolean(manageCountItem)}
        onClose={() => setManageCountItem(null)}
        onSubmit={handleSubmitCount}
        onCancel={handleCancelCount}
        itemData={
          manageCountItem
            ? {
                drugName: manageCountItem.drugName,
                strength: manageCountItem.strength,
                ndc: manageCountItem.ndc,
                manufacturer: manageCountItem.manufacturer,
                facilityName: manageCountItem.fulfillingPharmacy?.name ?? pharmacyName,
                facilityState: manageCountItem.fulfillingPharmacy?.state,
                inventoryItemId: manageCountItem.inventoryItemId,
                fulfillingPharmacyId:
                  manageCountItem.fulfillingPharmacy?.fulfillingPharmacyId ?? facilityId,
              }
            : null
        }
        submitLoading={submitCountLoading}
        cancelLoading={cancelCountLoading}
      />

      <Dialog
        open={Boolean(cancelLockConfirmItem)}
        onClose={() => setCancelLockConfirmItem(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Cancel Lock</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            Cancel the lock on{' '}
            <Box component='span' fontWeight={700}>
              {cancelLockConfirmItem?.drugName}
            </Box>
            ? Any count in progress will be discarded and the item will be available again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant='text'
            onClick={() => setCancelLockConfirmItem(null)}
            disabled={cancelCountLoading}
          >
            No, Keep It
          </Button>
          <Button
            variant='contained'
            disabled={cancelCountLoading}
            startIcon={cancelCountLoading ? <CircularProgress size={16} color='inherit' /> : null}
            sx={{ bgcolor: '#D97706', '&:hover': { bgcolor: '#B45309' } }}
            onClick={() =>
              handleCancelCount({ inventoryItemId: cancelLockConfirmItem?.inventoryItemId }).then(
                () => setCancelLockConfirmItem(null)
              )
            }
          >
            Cancel Lock
          </Button>
        </DialogActions>
      </Dialog>

      <CountHistoryModal
        open={Boolean(countHistoryItem)}
        onClose={() => setCountHistoryItem(null)}
        itemData={
          countHistoryItem
            ? {
                drugName: countHistoryItem.drugName,
                strength: countHistoryItem.strength,
                ndc: countHistoryItem.ndc,
                manufacturer: countHistoryItem.manufacturer,
                facilityName: countHistoryItem.fulfillingPharmacy?.name ?? pharmacyName,
                inventoryItemId: countHistoryItem.inventoryItemId,
                fulfillingPharmacyId: countHistoryItem.fulfillingPharmacy?.fulfillingPharmacyId,
              }
            : null
        }
      />

      {/* Lock confirm dialog */}
      <Dialog
        open={bulkLockConfirmOpen}
        onClose={() => setBulkLockConfirmOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Lock for Physical Count</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              p: 1.5,
              mb: 2,
              bgcolor: '#EFF6FF',
              border: '1px solid #BFDBFE',
              borderRadius: '6px',
            }}
          >
            <InfoOutlinedIcon
              sx={{ color: '#2563EB', fontSize: '1.1rem', mt: 0.1, flexShrink: 0 }}
            />
            <Box>
              <Typography variant='body2' sx={{ color: '#1e40af', mb: 0.5 }}>
                Starting a physical count will temporarily lock{' '}
                {unlockedSelected.length === 1
                  ? 'this item'
                  : `these ${unlockedSelected.length} items`}
                . While locked:
              </Typography>
              <Box component='ul' sx={{ pl: 2, m: 0 }}>
                {[
                  'Product fulfillment (WIP Init) is blocked',
                  'Manual Inventory Adjustment is blocked',
                  'Reversal Review allocation is blocked',
                ].map((text) => (
                  <Typography key={text} component='li' variant='body2' sx={{ color: '#1e40af' }}>
                    {text}
                  </Typography>
                ))}
              </Box>
              <Typography variant='body2' sx={{ color: '#1e40af', mt: 0.5 }}>
                The lock is released when you submit or cancel each count.
              </Typography>
            </Box>
          </Box>
          {unlockedSelected.length >= (items?.length ?? 0) && (
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
              All {unlockedSelected.length} item{unlockedSelected.length !== 1 ? 's' : ''} in this
              list are selected. Confirm to lock {unlockedSelected.length === 1 ? 'it' : 'them'} for
              physical count.
            </Typography>
          )}
          {unlockedSelected.length < (items?.length ?? 0) && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '6px',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 2,
                  px: 1.5,
                  py: 0.75,
                  bgcolor: '#F9FAFB',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant='caption'
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Drug Name
                </Typography>
                <Typography
                  variant='caption'
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  NDC
                </Typography>
              </Box>
              <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
                {unlockedSelected.map((row, idx) => (
                  <Box
                    key={row.inventoryItemId}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: 2,
                      px: 1.5,
                      py: 0.75,
                      bgcolor: idx % 2 === 0 ? '#fff' : '#F9FAFB',
                      borderBottom: idx < unlockedSelected.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant='body2'>{row.drugName}</Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.ndc}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant='text'
            onClick={() => setBulkLockConfirmOpen(false)}
            disabled={bulkOperationLoading}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleBulkLock}
            disabled={bulkOperationLoading}
            startIcon={bulkOperationLoading ? <CircularProgress size={16} color='inherit' /> : null}
          >
            Lock {unlockedSelected.length} Item{unlockedSelected.length !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlock confirm dialog */}
      <Dialog
        open={bulkUnlockConfirmOpen}
        onClose={() => setBulkUnlockConfirmOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Cancel Lock</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              p: 1.5,
              mb: 2,
              bgcolor: '#FFF7ED',
              border: '1px solid #FED7AA',
              borderRadius: '6px',
            }}
          >
            <WarningAmberIcon
              sx={{ color: '#D97706', fontSize: '1.1rem', mt: 0.1, flexShrink: 0 }}
            />
            <Box>
              <Typography variant='body2' sx={{ color: '#92400E', mb: 0.5 }}>
                Cancelling the lock on{' '}
                {lockedSelected.length === 1 ? 'this item' : `these ${lockedSelected.length} items`}{' '}
                will:
              </Typography>
              <Box component='ul' sx={{ pl: 2, m: 0 }}>
                {[
                  'Discard any physical count in progress',
                  'Re-enable fulfillment, adjustments, and allocations',
                ].map((text) => (
                  <Typography key={text} component='li' variant='body2' sx={{ color: '#92400E' }}>
                    {text}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>
          {lockedSelected.length >= (items?.length ?? 0) && (
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
              All {lockedSelected.length} item{lockedSelected.length !== 1 ? 's' : ''} in this list
              are selected. Confirm to cancel the lock and resume normal operations.
            </Typography>
          )}
          {lockedSelected.length < (items?.length ?? 0) && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '6px',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 2,
                  px: 1.5,
                  py: 0.75,
                  bgcolor: '#F9FAFB',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant='caption'
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Drug Name
                </Typography>
                <Typography
                  variant='caption'
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  NDC
                </Typography>
              </Box>
              <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
                {lockedSelected.map((row, idx) => (
                  <Box
                    key={row.inventoryItemId}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: 2,
                      px: 1.5,
                      py: 0.75,
                      bgcolor: idx % 2 === 0 ? '#fff' : '#F9FAFB',
                      borderBottom: idx < lockedSelected.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant='body2'>{row.drugName}</Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.ndc}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant='text'
            onClick={() => setBulkUnlockConfirmOpen(false)}
            disabled={bulkOperationLoading}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleBulkUnlock}
            disabled={bulkOperationLoading}
            startIcon={bulkOperationLoading ? <CircularProgress size={16} color='inherit' /> : null}
            sx={{ bgcolor: '#D97706', '&:hover': { bgcolor: '#B45309' } }}
          >
            Cancel Lock ({lockedSelected.length} Item{lockedSelected.length !== 1 ? 's' : ''})
          </Button>
        </DialogActions>
      </Dialog>

      <Grid component={Paper} sx={{ width: '100%', overflow: 'hidden' }}>
        {canManagePhysicalCount && selectionMode && (
          <BulkSelectionBar
            selectedCount={selectedRows.length}
            selectionMode={selectionMode}
            onLock={() => setBulkLockConfirmOpen(true)}
            onUnlock={() => setBulkUnlockConfirmOpen(true)}
            onClear={clearSelection}
          />
        )}
        <Box
          sx={{
            position: 'relative',
            height: '65vh',
            width: '100%',
            '& .ag-header': { backgroundColor: 'primary.main' },
            '& .ag-header-cell-text': { color: 'primary.contrastText', fontWeight: 600 },
            '& .ag-header-icon': { color: 'primary.contrastText' },
            '& .ag-header-cell-resize::after': {
              backgroundColor: colors.tableHeaderSeparator,
              width: '2px',
            },
            '& .ag-header-cell.inventory-actions-header': {
              borderLeft: `2px solid ${colors.tableHeaderSeparator}`,
              // The control plus its badge needs the full 50px the column has.
              padding: 0,
              justifyContent: 'center',
            },
            // AG Grid wraps a custom header in its own flex box, which defaults to the start.
            // Centring only the cell leaves the control sitting against the left edge.
            '& .ag-header-cell.inventory-actions-header .ag-header-cell-comp-wrapper': {
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            },
          }}
        >
          {/* A refresh no longer blanks the grid, so it needs its own sign that something is
              happening. Under the header and over the rows, which are the ones about to change. */}
          {loading && (
            <LinearProgress
              sx={{ position: 'absolute', top: GRID_HEADER_HEIGHT, left: 0, right: 0, zIndex: 1 }}
            />
          )}
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            sideBar={sideBar}
            enableCellTextSelection
            tooltipShowDelay={400}
            getRowStyle={getRowStyle}
            rowSelection='multiple'
            suppressRowClickSelection
            onSelectionChanged={handleSelectionChanged}
          />
        </Box>
      </Grid>
    </>
  );
};

InventoryDashboardFacilityTable.propTypes = {
  facilityId: PropTypes.string.isRequired,
};

export default InventoryDashboardFacilityTable;
